import type { WooStoreConfig } from "../types";

const ORGANIZATION_LOGO_URL =
  "https://fluxmed.cloud/StructureDefinition/OrganizationLogo";

const DEFAULT_SITE_ORIGIN = "https://fluxmed.cloud";

/** Env opcional — usada só para derivar siteOrigin (redirect de pagamento). */
type WooSiteOriginEnvKey = "VITE_WOOCOMMERCE_FLUXMED_CLOUD_BASE_URL";

export type OrganizationStoreAdapterRef = {
  /** slug interno estável (ex.: plant-based-labs) */
  storeSlug: string;
  /** Client ID OpenID Fluxmed — um por loja WordPress/WooCommerce. */
  openIdClientId: string;
  /**
   * Path (ou path+query) do callback OpenID no WordPress, relativo à base do site.
   * Default: `/wp-admin/admin-ajax.php?action=openid-connect-authorize`
   */
  openIdRedirectPath?: string;
  /** Base Woo/WordPress — pode incluir `/wp-json/wc/v3` no final. */
  siteOrigin?: string;
  siteOriginEnv?: WooSiteOriginEnvKey;
};

/** institutionId = parte após OID (ex.: 38288169000161) */
export const ORGANIZATION_STORE_REGISTRY: Record<
  string,
  OrganizationStoreAdapterRef
> = {
  /** Plant Based Labs — PLANT BASED MEDICAMENTOS E NUTRACEUTICOS LTDA */
  "38288169000161": {
    storeSlug: "plant-based-labs",
    openIdClientId: "rUBhyOGrPaaD8rMn35pXQYXHFfwa",
    openIdRedirectPath: "/oicd-flux-authorize",
    siteOrigin: "https://plantbased-labs.com/integracaoflux/wp-json/wc/v3",
  },
  "9876543210123": {
    storeSlug: "fluxmedcloud",
    openIdClientId: "nK_cSrkTXYWCkO4zgYerAmP1WsUa",
    siteOrigin: "https://fluxmed.cloud/wp-json/wc/v3",
  },
};

export const FLUXMED_OPENID_SCOPES = "openid email profile groups phone address";

function readEnv(key: WooSiteOriginEnvKey): string {
  return (import.meta.env[key] as string | undefined) ?? "";
}

function deriveSiteOrigin(baseUrl: string): string {
  return baseUrl.replace(/\/$/, "") || DEFAULT_SITE_ORIGIN;
}

function resolveSiteOrigin(entry: OrganizationStoreAdapterRef): string {
  if (entry.siteOrigin) return entry.siteOrigin.replace(/\/$/, "");
  if (entry.siteOriginEnv) {
    const baseUrl = readEnv(entry.siteOriginEnv);
    if (baseUrl) return deriveSiteOrigin(baseUrl);
  }
  return DEFAULT_SITE_ORIGIN;
}

export function extractOrganizationLogoDataUrl(
  org: fhir5.Organization,
): string | undefined {
  const extensions = org.extension ?? [];
  const logoExt = extensions.find((ext) =>
    ext.url?.includes("OrganizationLogo"),
  );
  const attachment = logoExt?.valueAttachment;
  if (!attachment?.contentType || !attachment?.data) return undefined;
  return `data:${attachment.contentType};base64,${attachment.data}`;
}

export function resolveOrganizationDisplayName(org: fhir5.Organization): string {
  const alias = org.alias?.[0];
  if (alias && alias !== "null") return alias;
  return org.name?.trim() || org.id || "Dispensador";
}

export function extractInstitutionId(organizationId: string): string | null {
  const trimmed = organizationId.trim();
  if (!trimmed) return null;
  const parts = trimmed.split("-");
  if (parts.length < 2) return trimmed;
  return parts[1] || null;
}

export type MarketplaceDispensaryConfig = WooStoreConfig & {
  organizationId: string;
  institutionId: string;
  status: "active" | "coming_soon";
};

export function resolveStoreConfigFromOrganization(
  org: fhir5.Organization,
): MarketplaceDispensaryConfig | null {
  if (!org.id) return null;

  const institutionId = extractInstitutionId(org.id);
  if (!institutionId) return null;

  const displayName = resolveOrganizationDisplayName(org);
  const logoDataUrl = extractOrganizationLogoDataUrl(org);
  const registryEntry = ORGANIZATION_STORE_REGISTRY[institutionId];

  if (!registryEntry) {
    return {
      id: institutionId,
      name: displayName,
      organizationId: org.id,
      institutionId,
      status: "coming_soon",
      active: false,
      logoDataUrl,
      logoUrl: logoDataUrl,
    };
  }

  return {
    id: registryEntry.storeSlug,
    name: displayName,
    organizationId: org.id,
    institutionId,
    status: "active",
    active: true,
    openIdClientId: registryEntry.openIdClientId,
    openIdRedirectPath: registryEntry.openIdRedirectPath,
    siteOrigin: resolveSiteOrigin(registryEntry),
    logoDataUrl,
    logoUrl: logoDataUrl,
  };
}

export function isStoreApiReady(store: WooStoreConfig): boolean {
  return (
    store.status !== "coming_soon" &&
    store.active !== false &&
    Boolean(store.institutionId?.trim())
  );
}

export { ORGANIZATION_LOGO_URL };
